import { useEffect, useMemo, useRef, useState } from "react";
import CommonModal, {
  CommonModalFooter,
} from "../shared/modalLayout/CommonModal";
import PrescriptionList from "../../features/prescription/PrescriptionList";
import { useGetPrescriptionQuery } from "../../app/prescriptionApi/prescriptionApi";
import {
  PRESCRIPTION_FORM_INITIAL,
  PRESCRIPTION_UPLOAD_ACCEPT,
  buildMultiLensPrescriptionDraft,
  getPrescriptionEntry,
  getPrescriptionLensCount,
  getPrescriptionUploadError,
  normalizeLensItems,
  prescriptionFormToVa,
  prescriptionHasEnteredValues,
  vaPrescriptionToFormValues,
} from "../../helpers/prescriptionFormat";
import {
  hasBlockingPrescriptionIssue,
  prescriptionHasAdd,
} from "../../helpers/prescriptionExtract";
import { useParsePrescription } from "../../hooks/useParsePrescription";
import PrescriptionExtractCompare from "./PrescriptionExtractCompare";
import { showToast } from "../../helpers/Utils";
import Button from "../shared/buttons/button";
import { UploadFileSvg } from "../../svgFiles/UploadFileSvg";
import { CircleCheckSvg } from "../../svgFiles/CircleCheckSvg";
import { FileSvg } from "../../svgFiles/FileSvg";
import Loader from "../shared/loader/Loader";
import "../../features/prescription/prescription.css";
import "../../styles/uploadPrescriptionStepModal.css";
import "../../styles/vaOrderModal.css";

const PRESCRIPTION_TYPE = "Progressive Lenses";

function getInitialFormValues(initialPrescription, lensIndex, isMultiLens) {
  if (initialPrescription?.skipped) {
    return {
      ...PRESCRIPTION_FORM_INITIAL,
      prescriptionType: PRESCRIPTION_TYPE,
    };
  }

  const entry = getPrescriptionEntry(
    initialPrescription,
    lensIndex,
    isMultiLens
  );

  if (entry?.skipped) {
    return {
      ...PRESCRIPTION_FORM_INITIAL,
      prescriptionType: PRESCRIPTION_TYPE,
    };
  }

  if (entry?.formValues || entry?.od) {
    return {
      ...vaPrescriptionToFormValues(entry),
      prescriptionType:
        entry?.formValues?.prescriptionType || PRESCRIPTION_TYPE,
    };
  }

  return {
    ...PRESCRIPTION_FORM_INITIAL,
    prescriptionType: PRESCRIPTION_TYPE,
  };
}

function getInitialPdOption(initialPrescription, lensIndex, isMultiLens) {
  if (initialPrescription?.skipped) return null;

  const entry = getPrescriptionEntry(
    initialPrescription,
    lensIndex,
    isMultiLens
  );

  if (entry?.skipped) return null;

  return (
    entry?.pdType ||
    entry?.formValues?.PDOneorTwoNumbers ||
    null
  );
}

function getInitialExtractMeta(initialPrescription, lensIndex, isMultiLens) {
  if (initialPrescription?.skipped) return null;
  const entry = getPrescriptionEntry(
    initialPrescription,
    lensIndex,
    isMultiLens
  );
  return entry?.extractMeta || (!isMultiLens ? initialPrescription?.extractMeta : null) || null;
}

function createInitialMap(lensCount, initialPrescription, isMultiLens, factory) {
  return Object.fromEntries(
    Array.from({ length: lensCount }, (_, index) => [
      index,
      factory(index),
    ])
  );
}

function withExtractFields(prescription, extract = {}) {
  return {
    ...prescription,
    skipped: false,
    extractMeta: extract.extractMeta ?? null,
    prescriptionFileKey: extract.prescriptionFileKey ?? null,
    multiplePrescriptions: extract.multiplePrescriptions === true,
    hasAdd: prescriptionHasAdd(prescription),
  };
}

export default function UploadPrescriptionStepModal({
  show,
  onClose,
  onBack,
  onContinue,
  initialPrescription,
  onDraftChange,
  lensItems = [],
}) {
  const fileInputRef = useRef(null);
  const activeLensIndexRef = useRef(0);
  const hasHydratedRef = useRef(false);
  const { parse: parseRx, isParsing } = useParsePrescription();
  const normalizedLensItems = useMemo(
    () => normalizeLensItems(lensItems),
    [lensItems]
  );
  const lensCount = getPrescriptionLensCount(normalizedLensItems);
  const isMultiLens = lensCount > 1;

  const [formValuesMap, setFormValuesMap] = useState(() =>
    createInitialMap(lensCount, initialPrescription, isMultiLens, (index) =>
      getInitialFormValues(initialPrescription, index, isMultiLens)
    )
  );
  const [selectedOptionMap, setSelectedOptionMap] = useState(() =>
    createInitialMap(lensCount, initialPrescription, isMultiLens, (index) =>
      getInitialPdOption(initialPrescription, index, isMultiLens)
    )
  );
  const [uploadedFileMap, setUploadedFileMap] = useState({});
  const [filePreviewUrlMap, setFilePreviewUrlMap] = useState(() =>
    createInitialMap(lensCount, initialPrescription, isMultiLens, (index) => {
      const entry = getPrescriptionEntry(
        initialPrescription,
        index,
        isMultiLens
      );
      return initialPrescription?.skipped ? null : entry?.fileUrl ?? null;
    })
  );
  const [extractMetaMap, setExtractMetaMap] = useState(() =>
    createInitialMap(lensCount, initialPrescription, isMultiLens, (index) =>
      getInitialExtractMeta(initialPrescription, index, isMultiLens)
    )
  );
  const [prescriptionFileKeyMap, setPrescriptionFileKeyMap] = useState(() =>
    createInitialMap(lensCount, initialPrescription, isMultiLens, (index) => {
      const entry = getPrescriptionEntry(
        initialPrescription,
        index,
        isMultiLens
      );
      return initialPrescription?.skipped
        ? null
        : entry?.prescriptionFileKey ||
            (!isMultiLens ? initialPrescription?.prescriptionFileKey : null) ||
            null;
    })
  );
  const [multiplePrescriptionsMap, setMultiplePrescriptionsMap] = useState(() =>
    createInitialMap(lensCount, initialPrescription, isMultiLens, (index) => {
      const entry = getPrescriptionEntry(
        initialPrescription,
        index,
        isMultiLens
      );
      return (
        !initialPrescription?.skipped &&
        (entry?.multiplePrescriptions === true ||
          (!isMultiLens && initialPrescription?.multiplePrescriptions === true))
      );
    })
  );
  const [parseErrorMap, setParseErrorMap] = useState({});

  useEffect(() => {
    if (!show) {
      hasHydratedRef.current = false;
      return;
    }
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;

    setFormValuesMap(
      createInitialMap(lensCount, initialPrescription, isMultiLens, (index) =>
        getInitialFormValues(initialPrescription, index, isMultiLens)
      )
    );
    setSelectedOptionMap(
      createInitialMap(lensCount, initialPrescription, isMultiLens, (index) =>
        getInitialPdOption(initialPrescription, index, isMultiLens)
      )
    );
    setUploadedFileMap({});
    setFilePreviewUrlMap(
      createInitialMap(lensCount, initialPrescription, isMultiLens, (index) => {
        const entry = getPrescriptionEntry(
          initialPrescription,
          index,
          isMultiLens
        );
        return initialPrescription?.skipped ? null : entry?.fileUrl ?? null;
      })
    );
    setExtractMetaMap(
      createInitialMap(lensCount, initialPrescription, isMultiLens, (index) =>
        getInitialExtractMeta(initialPrescription, index, isMultiLens)
      )
    );
    setPrescriptionFileKeyMap(
      createInitialMap(lensCount, initialPrescription, isMultiLens, (index) => {
        const entry = getPrescriptionEntry(
          initialPrescription,
          index,
          isMultiLens
        );
        return initialPrescription?.skipped
          ? null
          : entry?.prescriptionFileKey ||
              (!isMultiLens ? initialPrescription?.prescriptionFileKey : null) ||
              null;
      })
    );
    setMultiplePrescriptionsMap(
      createInitialMap(lensCount, initialPrescription, isMultiLens, (index) => {
        const entry = getPrescriptionEntry(
          initialPrescription,
          index,
          isMultiLens
        );
        return (
          !initialPrescription?.skipped &&
          (entry?.multiplePrescriptions === true ||
            (!isMultiLens && initialPrescription?.multiplePrescriptions === true))
        );
      })
    );
    setParseErrorMap({});
  }, [show, initialPrescription, lensCount, isMultiLens]);

  useEffect(() => {
    const urls = [];

    Object.entries(uploadedFileMap).forEach(([index, file]) => {
      if (!file) return;
      const url = URL.createObjectURL(file);
      urls.push(url);
      setFilePreviewUrlMap((prev) => ({ ...prev, [index]: url }));
    });

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [uploadedFileMap]);

  const { data: prescriptionData, isLoading } = useGetPrescriptionQuery({
    prescriptionType: PRESCRIPTION_TYPE,
  });

  const lensHasUploadedFile = (index) => {
    if (uploadedFileMap[index]) return true;
    const entry = getPrescriptionEntry(
      initialPrescription,
      index,
      isMultiLens
    );
    return Boolean(!initialPrescription?.skipped && entry?.fileName);
  };

  const lensCanConfirm = (index) =>
    prescriptionHasEnteredValues(
      formValuesMap[index],
      selectedOptionMap[index],
      lensHasUploadedFile(index)
    );

  const canConfirm = isMultiLens
    ? Array.from({ length: lensCount }, (_, index) => lensCanConfirm(index)).some(
        Boolean
      )
    : lensCanConfirm(0);

  const issues = Object.values(extractMetaMap).flatMap(
    (meta) => meta?.issues || []
  );
  const blocking = hasBlockingPrescriptionIssue(issues);

  const getExtractForIndex = (
    index,
    nextExtractMetaMap = extractMetaMap,
    nextFileKeyMap = prescriptionFileKeyMap,
    nextMultipleMap = multiplePrescriptionsMap
  ) => ({
    extractMeta: nextExtractMetaMap[index] ?? null,
    prescriptionFileKey: nextFileKeyMap[index] ?? null,
    multiplePrescriptions: nextMultipleMap[index] === true,
  });

  const buildDraft = (
    nextUploadedFileMap = uploadedFileMap,
    nextFormValuesMap = formValuesMap,
    nextSelectedOptionMap = selectedOptionMap,
    nextExtractMetaMap = extractMetaMap,
    nextFileKeyMap = prescriptionFileKeyMap,
    nextMultipleMap = multiplePrescriptionsMap
  ) => {
    if (isMultiLens) {
      const draft = buildMultiLensPrescriptionDraft({
        lensItems: normalizedLensItems,
        formValuesMap: nextFormValuesMap,
        selectedOptionMap: nextSelectedOptionMap,
        uploadedFileMap: nextUploadedFileMap,
        initialPrescription,
      });

      return {
        ...draft,
        prescriptions: draft.prescriptions.map((entry, index) =>
          entry?.skipped
            ? entry
            : withExtractFields(
                entry,
                getExtractForIndex(
                  index,
                  nextExtractMetaMap,
                  nextFileKeyMap,
                  nextMultipleMap
                )
              )
        ),
      };
    }

    const prescription = prescriptionFormToVa(
      nextFormValuesMap[0],
      nextSelectedOptionMap[0],
      nextUploadedFileMap[0] || null
    );

    if (
      !nextUploadedFileMap[0] &&
      !initialPrescription?.skipped &&
      initialPrescription?.fileName
    ) {
      prescription.fileName = initialPrescription.fileName;
      prescription.fileUrl = initialPrescription.fileUrl ?? null;
    }

    return withExtractFields(
      prescription,
      getExtractForIndex(
        0,
        nextExtractMetaMap,
        nextFileKeyMap,
        nextMultipleMap
      )
    );
  };

  const persistDraft = (
    nextFormValuesMap = formValuesMap,
    nextSelectedOptionMap = selectedOptionMap,
    nextUploadedFileMap = uploadedFileMap,
    nextExtractMetaMap = extractMetaMap,
    nextFileKeyMap = prescriptionFileKeyMap,
    nextMultipleMap = multiplePrescriptionsMap
  ) => {
    onDraftChange?.(
      buildDraft(
        nextUploadedFileMap,
        nextFormValuesMap,
        nextSelectedOptionMap,
        nextExtractMetaMap,
        nextFileKeyMap,
        nextMultipleMap
      )
    );
  };

  const handleSelect = (lensIndex, field, value) => {
    setFormValuesMap((prev) => {
      const next = {
        ...prev,
        [lensIndex]: { ...prev[lensIndex], [field]: value },
      };
      persistDraft(next, selectedOptionMap, uploadedFileMap);
      return next;
    });
  };

  const handlePdOptionChange = (lensIndex, option) => {
    setSelectedOptionMap((prev) => {
      const next = { ...prev, [lensIndex]: option };
      setFormValuesMap((formPrev) => {
        const nextForm = {
          ...formPrev,
          [lensIndex]: { ...formPrev[lensIndex], PDOneorTwoNumbers: option },
        };
        persistDraft(nextForm, next, uploadedFileMap);
        return nextForm;
      });
      return next;
    });
  };

  const applyExtractedPrescription = (lensIndex, extracted, file) => {
    const nextForm = vaPrescriptionToFormValues(extracted);
    const nextPd = extracted.pdType || null;
    const nextExtract = extracted.extractMeta || null;
    const nextFileKey = extracted.prescriptionFileKey || null;
    const nextMultiple = extracted.multiplePrescriptions === true;

    const nextFormValuesMap = {
      ...formValuesMap,
      [lensIndex]: nextForm,
    };
    const nextSelectedOptionMap = {
      ...selectedOptionMap,
      [lensIndex]: nextPd,
    };
    const nextUploadedFileMap = file
      ? { ...uploadedFileMap, [lensIndex]: file }
      : uploadedFileMap;
    const nextExtractMetaMap = {
      ...extractMetaMap,
      [lensIndex]: nextExtract,
    };
    const nextFileKeyMap = {
      ...prescriptionFileKeyMap,
      [lensIndex]: nextFileKey,
    };
    const nextMultipleMap = {
      ...multiplePrescriptionsMap,
      [lensIndex]: nextMultiple,
    };

    setFormValuesMap(nextFormValuesMap);
    setSelectedOptionMap(nextSelectedOptionMap);
    if (file) setUploadedFileMap(nextUploadedFileMap);
    setExtractMetaMap(nextExtractMetaMap);
    setPrescriptionFileKeyMap(nextFileKeyMap);
    setMultiplePrescriptionsMap(nextMultipleMap);
    setParseErrorMap((prev) => ({ ...prev, [lensIndex]: "" }));
    persistDraft(
      nextFormValuesMap,
      nextSelectedOptionMap,
      nextUploadedFileMap,
      nextExtractMetaMap,
      nextFileKeyMap,
      nextMultipleMap
    );
  };

  const handleContinue = () => {
    if (blocking || !canConfirm) return;
    const draft = buildDraft();
    onDraftChange?.(draft);
    onContinue(draft);
  };

  const handleSkip = () => {
    const skipped = { skipped: true };
    onDraftChange?.(skipped);
    onContinue(skipped);
  };

  const handleBack = () => {
    onDraftChange?.(buildDraft());
    onBack?.();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const error = getPrescriptionUploadError(file);
    if (error) {
      showToast(error, "error");
      return;
    }

    const lensIndex = activeLensIndexRef.current;
    const nextUploadedFileMap = { ...uploadedFileMap, [lensIndex]: file };
    const nextExtractMetaMap = { ...extractMetaMap, [lensIndex]: null };
    setUploadedFileMap(nextUploadedFileMap);
    setExtractMetaMap(nextExtractMetaMap);
    setParseErrorMap((prev) => ({ ...prev, [lensIndex]: "" }));

    try {
      const extracted = await parseRx({ file });
      applyExtractedPrescription(lensIndex, extracted, file);
    } catch (err) {
      setParseErrorMap((prev) => ({
        ...prev,
        [lensIndex]:
          err?.data?.message ||
          "Could not read prescription. Enter values manually and compare the file.",
      }));
      persistDraft(
        formValuesMap,
        selectedOptionMap,
        nextUploadedFileMap,
        nextExtractMetaMap
      );
    }
  };

  const openFilePicker = (lensIndex = 0) => {
    activeLensIndexRef.current = lensIndex;
    fileInputRef.current?.click();
  };

  const openUploadedFile = (lensIndex) => {
    const fileUrl = uploadedFileMap[lensIndex]
      ? filePreviewUrlMap[lensIndex]
      : getPrescriptionEntry(initialPrescription, lensIndex, isMultiLens)
          ?.fileUrl;

    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const renderLensSection = (lensIndex) => {
    const label =
      normalizedLensItems[lensIndex] ||
      (isMultiLens ? `Lens ${lensIndex + 1}` : null);
    const formValues = formValuesMap[lensIndex] || PRESCRIPTION_FORM_INITIAL;
    const selectedOption = selectedOptionMap[lensIndex] || null;
    const hasUploadedFile = lensHasUploadedFile(lensIndex);
    const uploadedFile = uploadedFileMap[lensIndex] || null;
    const fileName =
      uploadedFile?.name ||
      getPrescriptionEntry(initialPrescription, lensIndex, isMultiLens)
        ?.fileName ||
      "";
    const fileUrl = uploadedFile
      ? filePreviewUrlMap[lensIndex]
      : getPrescriptionEntry(initialPrescription, lensIndex, isMultiLens)
          ?.fileUrl;
    const extractMeta = extractMetaMap[lensIndex];
    const parseError = parseErrorMap[lensIndex];

    return (
      <div
        key={lensIndex}
        className={`upload-prescription-modal__section${
          isMultiLens ? " upload-prescription-modal__section--multi" : ""
        }`}
      >
        {label ? (
          <h3 className="upload-prescription-modal__lens-title">{label}</h3>
        ) : null}

        {(fileUrl || extractMeta) && (
          <PrescriptionExtractCompare
            fileUrl={fileUrl}
            fileName={fileName}
            fileType={uploadedFile?.type}
            extractMeta={extractMeta}
            issues={extractMeta?.issues || []}
          />
        )}
        {parseError ? <p className="text-danger small">{parseError}</p> : null}

        <PrescriptionList
          listData={prescriptionData}
          selectedOption={selectedOption}
          setSelectedOption={(option) => handlePdOptionChange(lensIndex, option)}
          handleSelect={(field, value) => handleSelect(lensIndex, field, value)}
          formValues={formValues}
          currentOrder={{ prescriptionType: PRESCRIPTION_TYPE }}
        />

        {isMultiLens ? (
          <div className="upload-prescription-modal__section-actions">
            <Button
              label={hasUploadedFile ? "Re-upload file" : "Upload file"}
              className="cancel upload-prescription-file-btn"
              icon={<UploadFileSvg />}
              onClick={() => openFilePicker(lensIndex)}
              disabled={isParsing}
            />
          </div>
        ) : null}

        {hasUploadedFile && fileName && !isParsing ? (
          <button
            type="button"
            className="upload-prescription-file-link"
            onClick={() => openUploadedFile(lensIndex)}
          >
            <FileSvg />
            <span>{fileName}</span>
          </button>
        ) : null}
      </div>
    );
  };

  const hasAnyUploadedFile = isMultiLens
    ? Array.from({ length: lensCount }, (_, index) =>
        lensHasUploadedFile(index)
      ).some(Boolean)
    : lensHasUploadedFile(0);

  return (
    <CommonModal
      show={show}
      setShow={(open) => {
        if (!open) onClose();
      }}
      title="Upload the prescription"
      centered
      className="upload-prescription-modal modal-lg"
      onHide={onClose}
      onBack={handleBack}
      closeOnBackdrop={false}
      footer={
        <CommonModalFooter
          align="center"
          className="upload-prescription-footer d-flex gap-3 mt-4"
        >
          {!isMultiLens ? (
            <Button
              label={hasAnyUploadedFile ? "Re-upload" : "Upload Prescription File"}
              className="cancel upload-prescription-file-btn"
              icon={<UploadFileSvg />}
              onClick={() => openFilePicker(0)}
              disabled={isParsing}
            />
          ) : null}
          <Button
            label="Confirm"
            icon={<CircleCheckSvg />}
            onClick={handleContinue}
            disabled={isParsing || blocking || !canConfirm}
          />
          <Button
            label="Skip"
            className="cancel"
            onClick={handleSkip}
            disabled={isParsing}
          />
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            accept={PRESCRIPTION_UPLOAD_ACCEPT}
            onChange={handleFileChange}
          />
        </CommonModalFooter>
      }
    >
      {isLoading || isParsing ? (
        <div className="text-center py-3">
          <Loader />
          {isParsing && (
            <p className="mb-0 mt-2 small text-muted">Reading prescription…</p>
          )}
        </div>
      ) : (
        <div className="upload-prescription-modal__grid">
          {Array.from({ length: lensCount }, (_, index) =>
            renderLensSection(index)
          )}
        </div>
      )}
    </CommonModal>
  );
}
